import React, {useState} from 'react';
import PropTypes from 'prop-types';
import {makeStyles} from 'tss-react/mui';
import {FormattedMessage} from 'react-intl';
import BoltIcon from '@mui/icons-material/Bolt';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import DocumentCard from './DocumentCard';
import SingleInsuranceHistoryCard from './SingleInsuranceHistoryCard';
import {formatPeriodDate} from '../utils/documentHelpers';

const useStyles = makeStyles()((theme) => ({
  historySection: {
    marginTop: 8,
    marginBottom: 24,
    textAlign: 'left',
  },
  historySectionTitle: {
    fontSize: theme.typography.pxToRem(17),
    fontWeight: 700,
    fontFamily: 'Ploni, Arial, sans-serif',
    color: theme.palette.text.primary,
    marginBottom: 16,
    textAlign: 'center',
  },
  pathACard: {
    border: `2px solid ${theme.palette.primary.main}`,
    borderRadius: 14,
    padding: '20px 16px',
    marginBottom: 16,
    backgroundColor: theme.palette.background.paper,
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    position: 'relative',
    '&:hover': {
      boxShadow: `0 2px 12px ${theme.palette.primary.main}26`,
    },
  },
  pathACardSelected: {
    boxShadow: `0 2px 16px ${theme.palette.primary.main}33`,
    borderWidth: 3,
  },
  pathACardDone: {
    borderColor: theme.palette.success.main,
    backgroundColor: theme.palette.success.light,
    cursor: 'default',
    '&:hover': {
      boxShadow: 'none',
    },
  },
  pathTitle: {
    fontSize: theme.typography.pxToRem(16),
    fontWeight: 700,
    fontFamily: 'Ploni, Arial, sans-serif',
    color: theme.palette.text.primary,
    marginBottom: 6,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'left',
    gap: 6,
  },
  pathDescription: {
    fontSize: theme.typography.pxToRem(14),
    fontFamily: 'Ploni, Arial, sans-serif',
    color: theme.palette.text.secondary,
    lineHeight: 1.6,
  },
  harHabituchDone: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    marginTop: 12,
    padding: '10px 0',
    borderTop: '1px solid #c8e6c8',
    color: theme.palette.success.main,
    fontSize: theme.typography.pxToRem(14),
    fontWeight: 600,
    fontFamily: 'Ploni, Arial, sans-serif',
  },
  harHabituchDoneIcon: {
    fontSize: theme.typography.pxToRem(20),
    color: theme.palette.success.main,
  },
  pathAButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    width: '100%',
    marginTop: 16,
    padding: '12px 24px',
    borderRadius: 25,
    border: 'none',
    backgroundColor: theme.palette.primary.main,
    color: '#fff',
    fontSize: theme.typography.pxToRem(15),
    fontWeight: 700,
    fontFamily: 'Ploni, Arial, sans-serif',
    cursor: 'pointer',
    transition: 'background-color 0.2s',
    '&:hover': {
      backgroundColor: theme.palette.primary.dark,
    },
  },
  pathBToggle: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    width: '100%',
    padding: '14px 16px',
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 10,
    backgroundColor: theme.palette.background.default,
    cursor: 'pointer',
    fontSize: theme.typography.pxToRem(14),
    fontFamily: 'Ploni, Arial, sans-serif',
    color: theme.palette.text.secondary,
    marginBottom: 16,
    direction: 'ltr',
    transition: 'all 0.2s ease',
    '&:hover': {
      borderColor: theme.palette.primary.main,
      color: theme.palette.primary.main,
    },
  },
  pathBContent: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 12,
    padding: 16,
    marginBottom: 16,
    backgroundColor: theme.palette.background.paper,
  },
  manualOptions: {
    display: 'flex',
    justifyContent: 'center',
    gap: 12,
    marginBottom: 20,
    flexWrap: 'wrap',
  },
  manualOption: {
    flex: '1 1 140px',
    maxWidth: 200,
    padding: '14px 10px',
    border: `2px solid ${theme.palette.divider}`,
    borderRadius: 10,
    backgroundColor: theme.palette.background.paper,
    textAlign: 'center',
    fontSize: theme.typography.pxToRem(13),
    fontFamily: 'Ploni, Arial, sans-serif',
    color: theme.palette.text.primary,
    lineHeight: 1.5,
    cursor: 'pointer',
    transition: 'all 0.2s',
    '&:hover': {
      borderColor: theme.palette.primary.main,
    },
  },
  manualOptionActive: {
    backgroundColor: `${theme.palette.primary.main}14`,
    borderColor: theme.palette.primary.main,
    fontWeight: 600,
  },
  yearRange: {
    fontSize: theme.typography.pxToRem(13),
    fontFamily: 'Ploni, Arial, sans-serif',
    color: theme.palette.text.secondary,
    textAlign: 'center',
    marginBottom: 8,
    marginTop: 16,
    padding: '8px 0',
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
}));

const InsuranceHistorySection = ({
  insuranceHistoryDocs,
  idIssuanceDateProvided,
  insuranceHistoryPath,
  setInsuranceHistoryPath,
  uploadedFiles,
  uploadingDocs,
  batchErrors,
  singleHistoryFiles,
  singleHistoryStatus,
  yearsRange,
  onOpenHarHabituch,
  onInsuranceFileSelected,
  onBatchSubmit,
  onSingleHistoryFileSelected,
  onRemoveFile,
}) => {
  const {classes} = useStyles();
  const [manualSubOption, setManualSubOption] = useState(null);
  const [manualExpanded, setManualExpanded] = useState(false);

  const isPathA = insuranceHistoryPath === 'har_habituch';

  const toggleManual = () => {
    if (!manualExpanded) {
      setInsuranceHistoryPath('manual');
    }
    setManualExpanded((prev) => !prev);
  };

  return (
    <div className={classes.historySection}>
      <div className={classes.historySectionTitle}>
        <FormattedMessage id='docCollection.upload.historyTitle' />
      </div>

      {/* If har habituch already completed — show success card */}
      {idIssuanceDateProvided && (
        <div className={`${classes.pathACard} ${classes.pathACardDone}`}>
          <div className={classes.pathTitle}>
            <FormattedMessage id='docCollection.upload.pathA.title' />
          </div>
          <div className={classes.harHabituchDone}>
            <CheckCircleOutlineIcon className={classes.harHabituchDoneIcon} />
            <FormattedMessage id='docCollection.upload.pathA.done' />
          </div>
        </div>
      )}

      {/* If not completed yet — show path chooser */}
      {!idIssuanceDateProvided && (
        <>
          {/* Path A — הר הביטוח (compact) */}
          <div
            className={`${classes.pathACard} ${isPathA ? classes.pathACardSelected : ''}`}
            onClick={onOpenHarHabituch}
            role='button'
            tabIndex={0}
          >
            <div className={classes.pathTitle}>
              <FormattedMessage id='docCollection.upload.pathA.title' />
            </div>
            <div className={classes.pathDescription}>
              <FormattedMessage id='docCollection.upload.pathA.description' />
            </div>
            <button type='button' className={classes.pathAButton}>
              <BoltIcon style={{fontSize: 18}} />
              <FormattedMessage id='docCollection.upload.pathA.cta' />
            </button>
          </div>

          {/* Path B — manual upload (collapsible toggle) */}
          <button
            type='button'
            className={classes.pathBToggle}
            onClick={toggleManual}
          >
            <FormattedMessage id='docCollection.upload.pathB.toggle' />
            {manualExpanded ? <ExpandLessIcon style={{fontSize: 20}} /> : <ExpandMoreIcon style={{fontSize: 20}} />}
          </button>

          {manualExpanded && (
            <div className={classes.pathBContent}>
              <div className={classes.pathDescription} style={{marginBottom: 16, textAlign: 'center'}}>
                <FormattedMessage id='docCollection.upload.pathB.description' values={{count: insuranceHistoryDocs.length}} />
              </div>

              <div className={classes.manualOptions}>
                <div
                  className={`${classes.manualOption} ${manualSubOption === 'single' ? classes.manualOptionActive : ''}`}
                  onClick={() => setManualSubOption('single')}
                  role='button'
                  tabIndex={0}
                >
                  <FormattedMessage id='docCollection.upload.pathB.singleOption' values={{count: insuranceHistoryDocs.length}} />
                </div>
                <div
                  className={`${classes.manualOption} ${manualSubOption === 'per_year' ? classes.manualOptionActive : ''}`}
                  onClick={() => setManualSubOption('per_year')}
                  role='button'
                  tabIndex={0}
                >
                  <FormattedMessage id='docCollection.upload.pathB.perYearOption' />
                </div>
              </div>

              {/* Single document for all years */}
              {manualSubOption === 'single' && (
                <SingleInsuranceHistoryCard
                  documentCount={insuranceHistoryDocs.length}
                  yearsRange={yearsRange}
                  status={singleHistoryStatus}
                  files={singleHistoryFiles}
                  uploading={!!uploadingDocs.single_history}
                  onFileSelected={onSingleHistoryFileSelected}
                />
              )}

              {/* Per-year documents */}
              {manualSubOption === 'per_year' && (
                <div>
                  {insuranceHistoryDocs.map((doc) => (
                    <div key={doc.id}>
                      <div className={classes.yearRange}>
                        {doc.periodStart ? `${formatPeriodDate(doc.periodStart)} - ${formatPeriodDate(doc.periodEnd)}` : ''}
                      </div>
                      <DocumentCard
                        document={doc}
                        uploadedFiles={uploadedFiles[doc.id] || []}
                        onFileSelected={onInsuranceFileSelected}
                        onRemoveFile={onRemoveFile}
                        onBatchSubmit={onBatchSubmit}
                        uploading={!!uploadingDocs[doc.id]}
                        batchError={batchErrors[doc.id]}
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

InsuranceHistorySection.propTypes = {
  batchErrors: PropTypes.object,
  idIssuanceDateProvided: PropTypes.bool,
  insuranceHistoryDocs: PropTypes.array.isRequired,
  insuranceHistoryPath: PropTypes.string,
  onBatchSubmit: PropTypes.func.isRequired,
  onInsuranceFileSelected: PropTypes.func.isRequired,
  onOpenHarHabituch: PropTypes.func.isRequired,
  onRemoveFile: PropTypes.func.isRequired,
  onSingleHistoryFileSelected: PropTypes.func.isRequired,
  setInsuranceHistoryPath: PropTypes.func.isRequired,
  singleHistoryFiles: PropTypes.array,
  singleHistoryStatus: PropTypes.string.isRequired,
  uploadedFiles: PropTypes.object,
  uploadingDocs: PropTypes.object,
  yearsRange: PropTypes.string,
};

export default InsuranceHistorySection;
